import { ReactNode, useState } from 'react';

import { FormControlLabel } from '@mui/material';

import {
  ChartIndicator,
  ChartIndicatorProps,
  Checkbox,
  Typography,
  Box,
  Stack,
} from '@cast/design-system';

type LegendItem = {
  name: string;
  label: string | ReactNode;
  checked?: boolean;
  color?: string;
  chartIndicatorProps?: ChartIndicatorProps;
};

type Props = {
  items: LegendItem[];
  state?: Record<LegendItem['name'], boolean>;
  onChange: (item: string) => void;
  layout?: 'horizontal' | 'vertical';
  align?: 'left' | 'center' | 'right';
  verticalAlign?: 'top' | 'middle' | 'bottom';
};

export const CheckboxLegend = (props: Props) => {
  const { onChange, items, layout, state } = props;
  const [checked, setChecked] = useState(
    items.reduce((acc, item: (typeof items)[number]) => {
      acc[item.name] = item.checked ?? true;
      return acc;
    }, {} as Record<(typeof items)[number]['name'], boolean>)
  );

  const handleOnChange = (item: string) => {
    onChange(item);
    setChecked((current) => {
      return {
        ...current,
        [item]: !current[item],
      };
    });
  };

  const horizontalAlign = (align?: Props['align']) => {
    switch (align) {
      case 'center':
        return 'center';
      case 'left':
        return 'flex-start';
      case 'right':
        return 'flex-end';
      default:
        return 'flex-start';
    }
  };

  const verticalAlign = (align?: Props['verticalAlign']) => {
    switch (align) {
      case 'middle':
        return 'center';
      case 'top':
        return 'flex-start';
      case 'bottom':
        return 'flex-end';
      default:
        return 'flex-start';
    }
  };

  return (
    <Box
      sx={{
        '& .MuiFormControlLabel-root': {
          marginRight: layout === 'horizontal' ? '44px' : 0,
          marginBottom: layout === 'horizontal' ? 0 : '44px',
        },
        '& .MuiFormControlLabel-root:last-of-type':
          layout === 'horizontal'
            ? {
                marginRight: 0,
              }
            : { marginBottom: 0 },
      }}
      display="flex"
      flexDirection={props.layout === 'horizontal' ? 'row' : 'column'}
      justifyContent={horizontalAlign(props.align)}
      alignItems={verticalAlign(props.verticalAlign)}
    >
      {items.map((item) => (
        <FormControlLabel
          sx={{
            gap: '8px',
          }}
          key={item.name}
          onChange={() => handleOnChange(item.name)}
          control={
            <Checkbox
              size="small"
              name={item.name}
              checked={state ? state[item.name] : checked[item.name]}
              testId={`${item.name}-chart-legend-filter`}
            />
          }
          label={
            <Stack direction="row" alignItems="center" gap="8px">
              <Typography variant="P12R">{item.label}</Typography>
              {item.chartIndicatorProps && (
                <ChartIndicator {...item.chartIndicatorProps} />
              )}
            </Stack>
          }
          labelPlacement="end"
        />
      ))}
    </Box>
  );
};
